// Phase 130 (Rule 1 + Codes Unification, Task 9) — claimed-state overlay.
//
// Renders inside the demo section when the SSE stream receives a one-shot
// `phone_claimed` event. The phone tied to this demo's sandboxSessionPublicId
// has been adopted by a signed-up HookMyApp account, the server has closed
// the stream, and there is no "Use Here" recovery path (it would dead-end).
//
// Composition mirrors demo-takeover-overlay.tsx — same DESIGN.md surface
// (#181A1B card, 24px radius, dual-shadow elevation, Meta Blue #0064E0 pill
// CTA at 100px radius, generous padding). Only the copy and CTA target
// differ: the takeover overlay calls `onReclaim`; this one routes the user
// to the app's sign-in flow.
//
// Sign-in target uses `buildHandoffUrl('')` — same per-environment app base
// URL the marketing nav already uses (production = app.hookmyapp.com,
// staging = staging.hookmyapp.com, local = the ngrok app tunnel). Visiting
// the app root unauthed lands on the WorkOS-AuthKit landing page where the
// user can sign in. There is no dedicated `/sign-in` route in the app.
//
// Copy: short, no em dashes (project memory rule). Button label is Title
// Case per the project memory rule.
import { buildHandoffUrl } from '../../lib/handoff';

interface DemoPhoneClaimedOverlayProps {
  message: string;
}

export function DemoPhoneClaimedOverlay({ message }: DemoPhoneClaimedOverlayProps) {
  const signInHref = buildHandoffUrl('');

  // Dismiss = full reload. The frontend's terminal state already cleared
  // localStorage + the publicId ref, so a reload re-bootstraps a clean
  // demo (fresh code + fresh stream). No client-only "hide overlay" path —
  // hiding without resetting would leave the chat panel visible behind a
  // stream that's already closed server-side.
  const dismiss = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) dismiss();
  };

  return (
    <div
      data-testid="demo-phone-claimed-overlay"
      onClick={handleBackdropClick}
      className="absolute inset-0 z-10 flex items-center justify-center px-6"
    >
      {/* Backdrop scrim — keeps the dimmed chat visible underneath. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-[24px] bg-black/55 backdrop-blur-sm"
      />
      {/* Card */}
      <div
        role="dialog"
        aria-labelledby="demo-phone-claimed-heading"
        aria-describedby="demo-phone-claimed-body"
        className="relative w-full max-w-md rounded-[24px] bg-[#181A1B] border border-white/10 p-8 md:p-10 text-center shadow-[0_12px_28px_rgba(0,0,0,0.2),0_2px_4px_rgba(0,0,0,0.1)]"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="demo-phone-claimed-close"
          className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/60 hover:bg-white/10 hover:text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            fill="none"
            className="h-4 w-4"
          >
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <h3
          id="demo-phone-claimed-heading"
          className="text-2xl font-medium tracking-[-0.01em] text-white"
        >
          Number Already Connected
        </h3>
        <p
          id="demo-phone-claimed-body"
          className="mt-3 text-base leading-relaxed text-white/65"
        >
          {message}
        </p>
        <a
          href={signInHref}
          data-testid="demo-phone-claimed-sign-in"
          className="mt-6 inline-flex h-11 items-center justify-center rounded-[100px] bg-[#0064E0] px-8 text-sm font-medium tracking-tight text-white transition hover:bg-[#0143B5] active:bg-[#004BB9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0064E0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#181A1B] shadow-[0_8px_24px_rgba(0,100,224,0.35)]"
        >
          Sign In To Your Dashboard
        </a>
      </div>
    </div>
  );
}
